import { FC, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import Chart from 'highcharts-react-official';
import {
  Checkbox,
  InlineLoading,
  DefinitionTooltip,
  InlineNotification,
} from '@carbon/react';
import { useAtomValue } from 'jotai';
import { dateRangeAtom, fetchCostComparison, isAbortError } from './atoms';
import { themed, CHART_PALETTE } from './chart-theme';
import { formatCost } from './format';
import type { CostComparison as CostComparisonData } from './types';
import styles from './page.module.scss';

// Blue stays reserved for the Calculated series; API-key columns use the rest.
const CALCULATED_COLOR = CHART_PALETTE[1]; // blue[60] #0f62fe

export const CostComparison: FC = () => {
  const [data, setData] = useState<CostComparisonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Which API keys are included in the actual total. Default: all of them.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const range = useAtomValue(dateRangeAtom);
  // Depend on the primitive bounds, not the `range` object, so the fetch
  // re-runs only when the dates actually change (not on identity churn).
  const { start, end } = range;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchCostComparison({ start, end }, controller)
      .then((res) => {
        if (controller.signal.aborted) return;
        setData(res);
        setSelected(new Set(res.keys.map((k) => k.id)));
      })
      .catch((e) => {
        if (controller.signal.aborted || isAbortError(e)) return;
        setError(e instanceof Error ? e.message : 'Failed to load comparison');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [start, end]);

  if (loading) {
    return (
      <div className={styles.comparisonCard}>
        <InlineLoading description="Loading actual cost from Anthropic…" />
      </div>
    );
  }
  if (error) {
    return (
      <div className={styles.comparisonCard}>
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Couldn't load actual cost"
          subtitle={error}
        />
      </div>
    );
  }
  if (!data) return null;

  if (!data.available) {
    return (
      <div className={styles.comparisonCard}>
        <div className={styles.chartTitle}>
          Calculated (agents) vs Actual (org-wide) cost
        </div>
        <p className={styles.comparisonNote}>
          Connect an Anthropic Admin API key (ANTHROPIC_ADMIN_KEY) to compare
          calculated agent cost against actual recorded usage.
        </p>
      </div>
    );
  }

  const toggle = (id: string, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  // Actual is a stacked column (one segment per selected API key) so the make-up of
  // the org-wide total is visible. Each key keeps its color by original index
  // regardless of toggles.
  const keyColors = CHART_PALETTE.filter((c) => c !== CALCULATED_COLOR);
  const selectedKeys = (data.keys ?? [])
    .map((key, i) => ({ key, color: keyColors[i % keyColors.length] }))
    .filter(({ key }) => selected.has(key.id));

  const actualSeries = selectedKeys.map(
    ({ key, color }): Highcharts.SeriesColumnOptions => ({
      type: 'column',
      name: key.name,
      data: (data.dates ?? []).map((_, di) => key.daily?.[di] ?? 0),
      color,
      stack: 'actual',
    })
  );

  // Sum the charted series data, not each key's raw `daily`, so the headline total
  // covers exactly the days shown even if a response carries more `daily` entries
  // than `dates`.
  const totalActual = actualSeries.reduce(
    (sum, series) => sum + (series.data as number[]).reduce((a, b) => a + b, 0),
    0
  );

  return (
    <div className={styles.comparisonCard}>
      <div className={styles.comparisonHeader}>
        <div>
          <div className={styles.chartTitle}>
            Calculated (agents) vs Actual (org-wide) cost
          </div>
          <div className={styles.comparisonNote}>
            Calculated = successful tracked agent runs only · Actual =
            whole-organization Anthropic usage for the selected API keys. Toggle
            keys below to isolate the agent-related spend.
          </div>
        </div>
        <div className={styles.comparisonTotals}>
          <span className={styles.comparisonMetric}>
            <span className={styles.comparisonLabel}>Calculated (agents)</span>
            {formatCost(data.totalCalculated)}
          </span>
          <span className={styles.comparisonMetric}>
            <span className={styles.comparisonLabel}>
              <DefinitionTooltip
                definition="Whole-organization Anthropic usage for the selected API keys — all traffic on those keys, not just these agents. The gap over Calculated includes non-agent organization traffic; deselect non-agent keys below to narrow it."
                openOnHover
              >
                Actual (org-wide)
              </DefinitionTooltip>
            </span>
            {formatCost(totalActual)}
          </span>
        </div>
      </div>

      {totalActual > data.totalCalculated && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Why Actual exceeds Calculated"
          subtitle="Actual is whole-organization usage on the selected keys, so it exceeds Calculated by design — it also includes non-agent traffic on those keys and the input tokens Anthropic bills for failed or retried agent runs, which Calculated can't price (only successful runs record usage)."
        />
      )}

      <fieldset className={styles.keyFilter}>
        <legend className={styles.keyFilterLegend}>API keys</legend>
        {(data.keys ?? []).map((k) => (
          <Checkbox
            key={k.id}
            id={`cost-key-${k.id}`}
            labelText={`${k.name} · ${formatCost(k.total)}`}
            checked={selected.has(k.id)}
            onChange={(
              _e: React.ChangeEvent<HTMLInputElement>,
              { checked }: { checked: boolean }
            ) => toggle(k.id, checked)}
          />
        ))}
      </fieldset>

      <Chart
        highcharts={Highcharts}
        options={themed({
          chart: { type: 'column', height: 300 },
          xAxis: {
            categories: (data.dates ?? []).map((d) => d.slice(5)),
            title: { text: 'day' },
          },
          yAxis: { min: 0, title: { text: 'cost ($)' } },
          legend: { enabled: true, events: { itemClick: () => false } },
          tooltip: { shared: true, valuePrefix: '$', valueDecimals: 2 },
          // Actual keys pile into one stacked column; Calculated is its own stack,
          // so the two sit side by side per day. The legend is a color key only:
          // clicking it can't hide a column, since the API-key checkboxes are the
          // single source of truth for what's included (and drive the headline total).
          plotOptions: { column: { stacking: 'normal' } },
          series: [
            {
              type: 'column',
              name: 'Calculated (agents)',
              data: data.calculated ?? [],
              color: CALCULATED_COLOR,
              stack: 'calculated',
            },
            ...actualSeries,
          ] as Highcharts.SeriesOptionsType[],
        })}
      />
      <div className={styles.chartNote}>
        Calculated may under-report when Anthropic 1-hour prompt caching is in
        use (cache writes are priced at the 5-minute rate), so part of the
        calculated-vs-actual gap is expected.
      </div>
    </div>
  );
};
